import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, colors, Stack, Typography } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isString } from 'lodash';

import { AppContext } from '../../context';
import { translate as t } from 'hooks/useTranslations';
import errorHelper from 'helpers/errorHelper';
import useManageNotification from 'views/App/Portal/Notifications/_data_sources/useManageNotification';
import useNotifications from 'views/App/Portal/Notifications/_data_sources/useNotifications';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import NotificationDetail from './detail';
import NotificationItem from './item';
import notificationsHelper from './notificationsHelper';
import type { Notification } from './_types';

const parseData = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return { data: jsonString };
  }
};

export default function Notifications() {
  const { setCurrentModule, setMainNavCollapsed, setBrowserTitle } = useContext(AppContext);

  const navigate = useNavigate();
  const notificationsQuery = useNotifications();
  const { id } = useParams();
  const { updateState } = useManageNotification();
  const queryClient = useQueryClient();
  const [notificationGroups, setNotificationGroups] = useState<{ [key: string]: Notification[] }>({});
  const [selectedNotificationCache, setSelectedNotificationCache] = useState<Record<string, Notification>>({});

  useEffect(() => {
    setCurrentModule('dashboard');
    setMainNavCollapsed(true);
    setBrowserTitle(t('notifications.title'));
  }, [setCurrentModule, setMainNavCollapsed, setBrowserTitle]);

  const notifications = useMemo(
    () =>
      notificationsQuery.data && notificationsQuery.data.pages
        ? notificationsQuery.data.pages.flatMap((x) =>
            x.content.map((x) => ({
              ...x,
              notificationData: isString(x.notificationData) ? parseData(x.notificationData) : x.notificationData,
            }))
          )
        : [],
    [notificationsQuery.data]
  );

  useEffect(() => {
    setNotificationGroups(notificationsHelper.groupNotifications(notifications));
  }, [notifications]);

  const selectedFromList = useMemo(() => notifications.find((x) => x.id === id), [notifications, id]);

  useEffect(() => {
    if (id && selectedFromList) {
      setSelectedNotificationCache((c) => ({ ...c, [id]: selectedFromList }));
    }
  }, [id, selectedFromList]);

  const selectedNotification = selectedFromList ?? (id ? selectedNotificationCache[id] : undefined);

  const toggleRead = useCallback(
    (notification: Notification) => {
      const nextState = notification.state === 'unread' ? 'read' : 'unread';
      setSelectedNotificationCache((c) => ({ ...c, [notification.id]: { ...notification, state: nextState } }));
      updateState(notification.id, nextState, () => {
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('notifications_unread_count');
      });
    },
    [queryClient, updateState]
  );

  const handleMarkedAsRead = useCallback((notification: Notification) => {
    setSelectedNotificationCache((c) => ({ ...c, [notification.id]: { ...notification, state: 'read' } }));
  }, []);

  const handleItemClick = useCallback(
    (notificationId: string) => {
      navigate(`/admin/notifications/${notificationId}`);
    },
    [navigate]
  );

  return (
    <CommonLayout
      pageTitle={t('notifications.title')}
      noPadding
    >
      <Box
        className='notifications'
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          height: 'calc(100vh - 130px)',
          backgroundColor: '#FFF',
        }}
      >
        <Box
          sx={{
            borderRight: `solid 1px ${colors.grey[100]}`,
            minHeight: 0,
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
          }}
        >
          <Box
            sx={{
              px: 4,
              py: 4,
              borderBottom: `solid 1px ${colors.grey[100]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='subtitle1'>{t('notifications.title')}</Typography>
          </Box>
          <Box
            sx={{
              height: '100%',
              overflowY: 'auto',
            }}
          >
            {notificationsQuery.isError ? (
              errorHelper.renderErrors(notificationsQuery)
            ) : (
              <>
                {Object.entries(notificationGroups).map(([group, items]) =>
                  items.length > 0 ? (
                    <Stack key={group}>
                      <Typography
                        component='div'
                        variant='overline'
                        sx={{
                          px: 4,
                          py: 1,
                          borderBottom: `solid 1px ${colors.grey[100]}`,
                        }}
                      >
                        {t(`notifications.groups.${group}`)}
                      </Typography>
                      {items.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          item={notification}
                          selected={id === notification.id}
                          onClick={handleItemClick}
                          toggleRead={toggleRead}
                        />
                      ))}
                    </Stack>
                  ) : null
                )}
              </>
            )}
          </Box>
        </Box>
        <Box>
          {selectedNotification && (
            <NotificationDetail
              notification={selectedNotification}
              onMarkedAsRead={handleMarkedAsRead}
              toggleRead={toggleRead}
            />
          )}
        </Box>
      </Box>
    </CommonLayout>
  );
}
